import { useEffect, useState } from "react";
import * as icons from "react-bootstrap-icons";
import {
  AUCTION_PLACEHOLDER,
  FallbackImage,
  TEAM_PLACEHOLDER,
} from "../../CommonComponent/FallbackImage";
import { ImageUrl } from "../../utils/config/config";
import { formatUsdWhole } from "../../utils/helper/money";
import { formatHms } from "../../utils/helper/lotClock";
import { formatStart } from "../../utils/helper/auctionList";
import {
  TEAM_STATES,
  formatStartsIn,
  hasBids,
  payoutRows,
  selectedTeamView,
  statusArea,
  teamListItem,
  teamState,
} from "../../utils/helper/auctionDetails";
import {
  bidAgeSeconds,
  bidTags,
  bidderInitials,
  bidderName,
  timeAgoText,
} from "../../utils/helper/recentBids";

/** The running team's clock turns red for its last 10 seconds (#237 AC10). */
export const URGENT_SECONDS = 10;

const BADGE_TEXT = { upcoming: "Upcoming", sold: "Sold", unsold: "Unsold" };

const money = (value) => (value === null || value === undefined ? "—" : formatUsdWhole(value));

/** The running team's clock in the left panel: its time left, or Paused while the auction is paused. */
const clockText = (lot, seconds) => (lot.status === "Paused" ? "Paused" : `${formatHms(seconds)} left`);

/**
 * One team in the left panel (#237 AC3). A click shows it in the selected team section; it changes
 * nothing about the auction.
 */
const TeamItem = ({ lot, selected, onSelect, running, seconds, nextTeamId, userId }) => {
  const item = teamListItem(lot, { nextTeamId, userId });
  const state = teamState(lot);
  const urgent = running && lot.status === "Live" && seconds <= URGENT_SECONDS;

  return (
    <button
      type="button"
      className={`auction-details-team${selected ? " active" : ""}`}
      aria-pressed={selected}
      onClick={() => onSelect(lot.teamId)}
    >
      <FallbackImage className="auction-details-team-thumb" image={lot.teamImage} placeholder={TEAM_PLACEHOLDER} />
      <span className="auction-details-team-body">
        <span className="auction-details-team-name">{lot.teamName}</span>
        {state === TEAM_STATES.LIVE ? (
          <span className={`auction-details-team-meta live${urgent ? " urgent" : ""}`}>
            <icons.Clock aria-hidden="true" /> {running ? clockText(lot, seconds) : lot.status}
          </span>
        ) : (
          <span className={`auction-details-team-meta ${state}`}>{item.meta}</span>
        )}
      </span>
      <span className="auction-details-team-amount">
        <b>{money(item.amount)}</b>
        <small>{item.amountLabel}</small>
      </span>
    </button>
  );
};

/**
 * The left panel, Teams in this Auction (#237 AC3, AC4): every team in three groups, searched by
 * name.
 */
export const TeamList = ({ lots, groups, query, onQuery, selectedId, onSelect, runningTeamId, seconds, nextTeamId, userId }) => (
  <section className="auction-details-panel auction-details-teams" aria-label="Teams in this auction">
    <div className="auction-details-list-head">
      <div className="auction-details-list-title">
        <b>Teams in this Auction</b>
        <span className="auction-details-pill">
          {lots.length} {lots.length === 1 ? "team" : "teams"}
        </span>
      </div>
      <label className="auction-details-search">
        <icons.Search aria-hidden="true" />
        <input
          type="search"
          placeholder="Search teams..."
          aria-label="Search teams"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
        />
      </label>
    </div>
    {groups.map((group) => (
      <div key={group.key} role="group" aria-label={group.label}>
        <div className="auction-details-group-head">
          <span className={`auction-details-dot ${group.key}`} aria-hidden="true" />
          {group.label}
          <span className="auction-details-count">{group.items.length}</span>
        </div>
        {group.items.length > 0 ? (
          group.items.map((lot) => (
            <TeamItem
              key={lot.teamId}
              lot={lot}
              selected={lot.teamId === selectedId}
              onSelect={onSelect}
              running={lot.teamId === runningTeamId}
              seconds={seconds}
              nextTeamId={nextTeamId}
              userId={userId}
            />
          ))
        ) : (
          <div className="auction-details-no-match">{group.empty}</div>
        )}
      </div>
    ))}
  </section>
);

/**
 * The auction header (#237 AC5, AC6): image, name, contest and sport, manager and start; on the
 * right the LIVE label, or the "Starts in" countdown before the start, or Closed once every team is
 * sold.
 */
export const AuctionHeader = ({ details, status, startsInSeconds }) => {
  const area = statusArea(status);
  const start = formatStart(details.startDate);

  return (
    <div className="auction-details-head">
      <FallbackImage
        className="auction-details-hero"
        image={details.auctionImage}
        placeholder={AUCTION_PLACEHOLDER}
        alt={details.auctionName || ""}
      />
      <div className="auction-details-head-info">
        <h2 className="auction-details-title">{details.auctionName}</h2>
        <div className="auction-details-sub">
          {details.tournamentName} • {details.sportName}
        </div>
        <div className="auction-details-meta">
          <span>
            Manager: <strong>{details.managerName}</strong>
          </span>
          <span>
            {area === "upcoming" ? "Starts" : "Started"} {start}
          </span>
        </div>
      </div>
      <div className="auction-details-status">
        {area === "live" && (
          <span className="auction-details-live" role="status">
            LIVE
          </span>
        )}
        {area === "upcoming" && (
          <>
            <div className="auction-details-starts" role="timer" aria-label="Starts in">
              <small>Starts in</small>
              <b>{formatStartsIn(startsInSeconds)}</b>
            </div>
            <span>Starts {start}</span>
          </>
        )}
        {area === "closed" && <span className="auction-badge closed">Closed</span>}
      </div>
    </div>
  );
};

/** The stats row (#237 AC8). */
export const StatsRow = ({ stats }) => (
  <div className="auction-details-stats">
    <div className="auction-details-stat">
      <b>{stats.teams}</b>
      <span>Teams</span>
    </div>
    <div className="auction-details-stat">
      <b>{formatUsdWhole(stats.pool)}</b>
      <span>Total Pool</span>
    </div>
    <div className="auction-details-stat">
      <b>{stats.totalBids}</b>
      <span>Total Bids</span>
    </div>
    <div className="auction-details-stat">
      <b>{stats.bidTimeLimit}</b>
      <span>Bid Time Limit</span>
    </div>
  </div>
);

const Field = ({ label, value, blue = false }) => (
  <div className="auction-details-field">
    <small>{label}</small>
    <b className={blue ? "blue" : undefined}>{value}</b>
  </div>
);

/**
 * The selected team section (#237 AC10-AC13): the team's state, its card and its four fields.
 */
export const SelectedTeam = ({ lot, teamCount, sportName, secondaryMarket, running, seconds, userId }) => {
  const view = selectedTeamView(lot, { userId });
  const urgent = running && lot.status === "Live" && seconds <= URGENT_SECONDS;

  return (
    <div className="auction-details-sec">
      <div className="auction-details-sec-head">
        <h3>{view.heading}</h3>
        {teamState(lot) === TEAM_STATES.LIVE ? (
          <div
            className={`auction-details-timer${urgent ? " urgent" : ""}`}
            role="timer"
            aria-label="Time left for this team"
          >
            <small>
              <icons.Stopwatch aria-hidden="true" />
              {lot.status === "Paused" ? "Paused" : "Time left for this team"}
            </small>
            <b>{formatHms(running ? seconds : 0)}</b>
          </div>
        ) : (
          <span className={`auction-badge ${view.badge === "upcoming" ? "upcoming" : "closed"}`}>
            {BADGE_TEXT[view.badge]}
          </span>
        )}
      </div>
      <div className="auction-details-card">
        <FallbackImage
          className="auction-details-card-image"
          image={lot.teamImage}
          placeholder={TEAM_PLACEHOLDER}
          alt={lot.teamName || ""}
        />
        <div className="auction-details-card-info">
          <div className="auction-details-card-name">{lot.teamName}</div>
          <div className="auction-details-sub">
            Team {lot.position} of {teamCount} • {sportName}
          </div>
          <div className="auction-details-market">
            Secondary Market
            <span className={`auction-badge ${secondaryMarket ? "available" : "unavailable"}`}>
              {secondaryMarket ? "Available" : "Unavailable"}
            </span>
          </div>
        </div>
        <div className="auction-details-card-bid">
          <small>{view.valueLabel}</small>
          <b className={view.neutral ? "neutral" : undefined}>{money(view.value)}</b>
          <span>{view.line}</span>
        </div>
      </div>
      <div className="auction-details-fields">
        <Field label="Starting Bid" value={money(lot.startingBid)} />
        <Field label="Next Minimum Bid" value={money(view.nextMinimumBid)} blue />
        <Field label="Bid Increment" value={money(lot.bidIncrement)} />
        <Field label="Total Placed Bids" value={Number(lot.totalPlacedBids) || 0} />
      </div>
    </div>
  );
};

/**
 * Auction Winnings (#237 AC14): each payout place with its share of the pool, from the auction's
 * payout settings (Auction/WinnerPercentage); the amounts are the server's.
 */
/**
 * A bidder's round photo, or their initials when they have none or it fails to load (#239 AC4).
 */
const BidderAvatar = ({ bid }) => {
  const [failed, setFailed] = useState(false);
  const initials = bidderInitials(bid);
  if (!bid.bidderImage || failed) {
    return (
      <span className="auction-details-rbid-avatar" aria-hidden="true">
        {initials}
      </span>
    );
  }
  return (
    <span className="auction-details-rbid-avatar" aria-hidden="true">
      <img src={`${ImageUrl}${bid.bidderImage}`} alt="" loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
};

/** The time-ago lines tick every second while there are rows (#239 AC8). */
const useSecondTick = (active) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
};

/**
 * Recent Bids (#239, mockup v7 States 4, 5, 7, 12): the selected team's five newest bids, newest
 * first, in the bottom row's left column. Each row shows the bidder's photo or initials, their name
 * with its tags (You; Highest while the team is in Bidding now, Winner once it is sold), how long
 * ago, and the amount; the lot's top bid is highlighted. Read-only, and the same for a player who has
 * not unlocked the auction (AC13).
 *
 * @param {object} props
 * @param {object} props.lot The selected lot.
 * @param {{bids: object[], serverNowUtc: (string|null), receivedAt: number, loading: boolean}} props.recent useRecentBids' answer.
 */
export const RecentBids = ({ lot, recent }) => {
  const now = useSecondTick(recent.bids.length > 0);

  let body;
  if (recent.bids.length > 0) {
    body = (
      <ul className="auction-details-bids">
        {recent.bids.map((bid) => {
          const seconds = bidAgeSeconds(bid, recent, now);
          return (
            <li key={bid.bidId} className={`auction-details-rbid${bid.isTopBid ? " lead" : ""}`}>
              <BidderAvatar bid={bid} />
              <div className="auction-details-rbid-who">
                <div className="auction-details-rbid-name">
                  <span>{bidderName(bid)}</span>
                  {bidTags(bid, lot).map((tag) => (
                    <span key={tag.label} className={`auction-details-tag ${tag.key}`}>
                      {tag.label}
                    </span>
                  ))}
                </div>
                <div className="auction-details-rbid-ago">{timeAgoText(seconds, bid.bidDateTime)}</div>
              </div>
              <div className="auction-details-rbid-amt">{formatUsdWhole(bid.bidAmount)}</div>
            </li>
          );
        })}
      </ul>
    );
  } else if (!hasBids(lot) && Number(lot.totalPlacedBids || 0) === 0) {
    body = <div className="auction-details-info auction-details-bids-empty">No bids yet for this team.</div>;
  } else {
    // The team has bids and they are on their way (or the read got no reply): nothing yet, never
    // "No bids yet" for a team that has some.
    body = null;
  }

  return (
    <div className="auction-details-recent">
      <h3>
        Recent Bids <span>• {lot.teamName}</span>
      </h3>
      {body}
    </div>
  );
};

export const AuctionWinnings = ({ pool, winnings }) => (
  <div className="auction-details-winnings">
    <h3>Auction Winnings</h3>
    <div className="auction-details-win">
      <span>Paid out from the total pool of {formatUsdWhole(pool)}</span>
      {payoutRows(winnings).map((row) => (
        <div className="auction-details-win-row" key={row.place}>
          <span>
            Place {row.place} – {row.percentage}%
          </span>
          <b>{money(row.amount)}</b>
        </div>
      ))}
      <span>Places and percentages come from the auction payout settings.</span>
    </div>
  </div>
);
